import { Config } from '@/config';
import type { PaddleCheckoutEvent } from '@/types';

let paddleReady = false;
let loadPromise: Promise<boolean> | null = null;

// ─── Script loader ───

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

// ─── Public API ───

/**
 * Start loading & initializing the Paddle Billing SDK.
 * Call once on page load. Does not block — returns immediately.
 * Internally stores a promise so openCheckout() can await it.
 */
export function initPaddle(
  eventCallback: (event: PaddleCheckoutEvent) => void,
): void {
  if (Config.PADDLE_DISABLED) {
    console.info(
      '%c[Paddle] disabled — SDK not loaded.',
      'color:#ff0033;font-weight:700',
      'The client token is a LIVE one, so local mode keeps it out of the page.',
      'Force it on with ?paddle=1.',
    );
    return;
  }

  if (paddleReady || loadPromise) return;

  loadPromise = (async (): Promise<boolean> => {
    try {
      await loadScript('https://cdn.paddle.com/paddle/v2/paddle.js');

      if (!window.Paddle) {
        console.error('[Paddle] window.Paddle is undefined after script load');
        return false;
      }

      window.Paddle.Environment.set(Config.PADDLE_ENV);
      window.Paddle.Initialize({
        token: Config.PADDLE_CLIENT_TOKEN,
        eventCallback: (event) => {
          console.info('[Paddle] Event:', event.name, JSON.stringify(event.data ?? {}, null, 2));
          eventCallback(event);
        },
      });

      paddleReady = true;
      console.info('[Paddle] Initialized successfully (env:', Config.PADDLE_ENV + ')');
      console.info('[Paddle] Client token:', Config.PADDLE_CLIENT_TOKEN.slice(0, 10) + '…');
      return true;
    } catch (err) {
      console.error('[Paddle] Init failed:', err);
      return false;
    }
  })();
}

/**
 * Open Paddle checkout overlay.
 * If SDK is still loading, waits up to 8 seconds for it.
 */
export async function openCheckout(
  priceId: string,
  email?: string,
  userId?: number,
): Promise<boolean> {
  if (Config.PADDLE_DISABLED) {
    console.warn('[Paddle] Checkout suppressed — Paddle is disabled in local mode.');
    return false;
  }

  // Wait for SDK if it's still loading
  if (!paddleReady && loadPromise) {
    const result = await Promise.race([
      loadPromise,
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 8000)),
    ]);
    if (!result) {
      console.error('[Paddle] SDK not ready after 8s');
      return false;
    }
  }

  if (!paddleReady || !window.Paddle) {
    console.error('[Paddle] SDK not available');
    return false;
  }

  const options: {
    items: Array<{ priceId: string; quantity: number }>;
    customer?: { email?: string };
    customData?: Record<string, string>;
    settings?: Record<string, unknown>;
  } = {
    items: [{ priceId, quantity: 1 }],
    settings: {
      displayMode: 'overlay',
      theme: 'light',
      locale: 'en',
    },
  };

  if (email && email.trim().length > 0) {
    options.customer = { email: email.trim() };
  }

  if (userId && userId > 0) {
    options.customData = { userId: String(userId) };
  }

  console.info('[Paddle] Opening checkout:', JSON.stringify(options, null, 2));

  try {
    window.Paddle.Checkout.open(options);
    return true;
  } catch (err) {
    console.error('[Paddle] Checkout.open() failed:', err);
    return false;
  }
}
